import { classNames, iconProps } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, { forwardRef } from 'react'
import { noop } from './menu'
import { FOCUS_BEHAVIOURS, getMenu, useNavMenuItem } from './NavMenuItem'

/**
 * Create a React Nav Item component
 *
 * @param {import('./interfaces').NavItemProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function navItem (
  {
    className,
    isMenuOpen,
    onClose,
    onKeyDown,
    onOpen,
    onSelect,
    orientation,
    withRightDivider,
    ...rest
  },
  ref
) {
  const isVertical = orientation === 'vertical'
  const {
    content,
    focus,
    hasMenuItems,
    isOpenByKeyboard,
    navItemsRef
  } = useNavMenuItem(
    {
      ...rest,
      className: classNames(className, {
        'zb-nav-item-is-open': isMenuOpen,
        'zb-nav-item-has-rightDivider': withRightDivider
      }),
      classPrefix: 'zb-nav-item',
      iconClass: 'zb-nav-item-right-icon',
      iconName: isMenuOpen ? 'chev-up-xsmall' : 'chev-down-xsmall',
      isMenuOpen,
      onClose,
      onItemKeyDown: handleItemKeydown,
      onKeyDown: handleKeyDown,
      onOpen,
      onSelect,
      orientation
    },
    ref
  )

  return content

  /**
   * @param {import('react').KeyboardEvent} event
   */
  function handleKeyDown (event) {
    const { key } = event
    switch (key) {
      case 'Enter':
        if (hasMenuItems) {
          if (isMenuOpen) {
            onClose()
          } else {
            isOpenByKeyboard.current = true
            onOpen()
            const menu = getMenu(navItemsRef.current)
            menu && menu.focus(0)
          }
          event.preventDefault()
        } else {
          onSelect(event)
        }
        break
      case 'Escape':
        if (hasMenuItems && isMenuOpen) {
          onClose()
        }
        break
      case 'ArrowDown': // falls through
      case 'ArrowRight':
        if (isVertical ? key === 'ArrowRight' : key === 'ArrowDown') {
          if (!isMenuOpen) {
            isOpenByKeyboard.current = true
            onOpen()
          }
          const menu = getMenu(navItemsRef.current)
          menu && menu.focus(0)

          event.preventDefault()
        }
        break
      case 'ArrowUp': // falls through
      case 'ArrowLeft':
        if (isVertical ? key === 'ArrowLeft' : key === 'ArrowUp') {
          if (!isMenuOpen) {
            isOpenByKeyboard.current = true
            onOpen()
          }
          const menu = getMenu(navItemsRef.current)
          menu && menu.focus(-1)
          event.preventDefault()
        }
        break
    }
    onKeyDown(event)
  }

  /**
   * @type {import('./interfaces').OnItemKeyDown}
   */
  function handleItemKeydown (event, groupIndex, focusedIndex) {
    const { key } = event
    switch (key) {
      case 'Escape':
        if (isMenuOpen) {
          onClose()
          focus()
        }
        break
      case 'ArrowUp': // falls through
      case 'ArrowLeft':
        if (
          groupIndex === 0 &&
          focusedIndex === -1 &&
          (isVertical ? key === 'ArrowLeft' : key === 'ArrowUp')
        ) {
          focus()
          event.preventDefault()
        }
        break
    }
  }
}

const NavItem = forwardRef(navItem)

NavItem.propTypes = {
  autoFocusBehaviour: PropTypes.oneOf(Object.values(FOCUS_BEHAVIOURS)),
  iconProps,
  isMenuOpen: PropTypes.bool,
  isOpenOnHover: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onKeyDown: PropTypes.func,
  onOpen: PropTypes.func,
  onSelect: PropTypes.func,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  renderLabel: PropTypes.func,
  withAutoOpen: PropTypes.bool,
  withRightDivider: PropTypes.bool
}

NavItem.defaultProps = {
  autoFocusBehaviour: FOCUS_BEHAVIOURS.FOCUS_FIRST_ITEM,
  isMenuOpen: false,
  isOpenOnHover: false,
  isSelected: false,
  onClick: noop,
  onClose: noop,
  onKeyDown: noop,
  onOpen: noop,
  onSelect: noop,
  renderLabel: ({ onSelect, ...props }) => <a {...props} onClick={onSelect} />,
  withRightDivider: false
}

NavItem.displayName = 'NavItem'
NavItem.componentName = 'zb.NavItem'

export default NavItem
